import { RefObject, useCallback, useEffect, useRef } from 'react';

interface UseHoverZoneDismissOptions {
  isOpen: boolean;
  onClose: () => void;
  getAnchorRect: () => DOMRect | null;
  panelRef: RefObject<HTMLElement | null>;
  closeDelayMs?: number;
}

const pointInRect = (x: number, y: number, rect: DOMRect): boolean =>
  x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

const getBridgeRect = (anchorRect: DOMRect, panelRect: DOMRect): DOMRect | null => {
  const left = Math.min(anchorRect.left, panelRect.left);
  const right = Math.max(anchorRect.right, panelRect.right);
  const width = right - left;
  if (width <= 0) return null;

  if (panelRect.top >= anchorRect.bottom) {
    const height = panelRect.top - anchorRect.bottom;
    if (height <= 0) return null;
    return new DOMRect(left, anchorRect.bottom, width, height);
  }

  if (panelRect.bottom <= anchorRect.top) {
    const height = anchorRect.top - panelRect.bottom;
    if (height <= 0) return null;
    return new DOMRect(left, panelRect.bottom, width, height);
  }

  return null;
};

const isPointerInZone = (
  x: number,
  y: number,
  getAnchorRect: () => DOMRect | null,
  panelRef: RefObject<HTMLElement | null>,
): boolean => {
  const anchorRect = getAnchorRect();
  const panelRect = panelRef.current?.getBoundingClientRect() ?? null;

  if (anchorRect && pointInRect(x, y, anchorRect)) return true;
  if (panelRect && pointInRect(x, y, panelRect)) return true;

  if (anchorRect && panelRect) {
    const bridge = getBridgeRect(anchorRect, panelRect);
    if (bridge && pointInRect(x, y, bridge)) return true;
  }

  return false;
};

export const useHoverZoneDismiss = ({
  isOpen,
  onClose,
  getAnchorRect,
  panelRef,
  closeDelayMs = 0,
}: UseHoverZoneDismissOptions) => {
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCloseRef = useRef(onClose);
  const getAnchorRectRef = useRef(getAnchorRect);

  onCloseRef.current = onClose;
  getAnchorRectRef.current = getAnchorRect;

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    if (closeDelayMs <= 0) {
      onCloseRef.current();
      return;
    }
    closeTimerRef.current = setTimeout(() => onCloseRef.current(), closeDelayMs);
  }, [cancelClose, closeDelayMs]);

  useEffect(() => {
    if (!isOpen) {
      cancelClose();
      return undefined;
    }

    const handlePointerMove = (e: PointerEvent) => {
      if (isPointerInZone(e.clientX, e.clientY, getAnchorRectRef.current, panelRef)) {
        cancelClose();
        return;
      }
      scheduleClose();
    };

    const handleDocumentLeave = () => {
      cancelClose();
      onCloseRef.current();
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.documentElement.addEventListener('mouseleave', handleDocumentLeave);

    return () => {
      cancelClose();
      document.removeEventListener('pointermove', handlePointerMove);
      document.documentElement.removeEventListener('mouseleave', handleDocumentLeave);
    };
  }, [isOpen, cancelClose, scheduleClose, panelRef]);
};
